import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Globe2, BookOpen, HelpCircle, Heart, Compass, Building2, Map, PenLine,
  KeyRound, Image as ImageIcon, CalendarClock, Plus, Pencil, Trash2,
  ExternalLink, Search, LogOut, Wand2, Loader2,
} from "lucide-react";

const TYPE_META = {
  destination: { label: "Destinations", icon: Globe2 },
  guide: { label: "Guides de voyage", icon: BookOpen },
  faq: { label: "FAQ", icon: HelpCircle },
  honeymoon: { label: "Voyages de noces", icon: Heart },
  sur_mesure: { label: "Voyages sur mesure", icon: Compass },
  circuit: { label: "Circuits", icon: Map },
  hotel: { label: "Hôtels", icon: Building2 },
  inspiration: { label: "Inspirations", icon: PenLine },
  pays: { label: "Pays", icon: Globe2 },
  region: { label: "Régions", icon: Map },
  ville: { label: "Villes", icon: Building2 },
  conseils: { label: "Conseils pratiques", icon: BookOpen },
  blog: { label: "Blog", icon: PenLine },
};

const STATUS_LABEL = {
  draft: { label: "Brouillon", cls: "bg-[#EAE8E3] text-[#737373]" },
  scheduled: { label: "Programmé", cls: "bg-[#E4DCCF] text-[#1A1B18]" },
  published: { label: "Publié", cls: "bg-[#1A1B18] text-white" },
  archived: { label: "Archivé", cls: "bg-[#F4E0D9] text-[#7A4B3B]" },
};

export default function AdminContent() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [prefixes, setPrefixes] = useState({});
  const [view, setView] = useState("destination"); // content_type | "keywords" | "media" | "scheduled"
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/admin/content-templates").then(({ data }) => {
      setTemplates(data);
      setPrefixes(Object.fromEntries(data.map((t) => [t.content_type, t.url_prefix])));
    }).catch(() => toast.error("Impossible de charger les modèles."));
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (view === "keywords") {
        const { data } = await api.get("/admin/keywords");
        setItems(data);
      } else if (view === "media") {
        const { data } = await api.get("/admin/media");
        setItems(data);
      } else if (view === "scheduled") {
        const { data } = await api.get("/admin/content", { params: { status: "scheduled" } });
        setItems(data);
      } else {
        const { data } = await api.get("/admin/content", { params: { content_type: view } });
        setItems(data);
      }
    } catch {
      toast.error("Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { refresh(); setSearch(""); }, [refresh]);

  const createItem = async () => {
    try {
      const { data } = await api.post("/admin/content", { content_type: view, title: "Nouveau contenu" });
      navigate(`/admin/content/${data.id}`);
    } catch {
      toast.error("Création impossible.");
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Supprimer ce contenu définitivement ?")) return;
    try {
      await api.delete(`/admin/content/${id}`);
      setItems((s) => s.filter((i) => i.id !== id));
      toast.success("Contenu supprimé.");
    } catch { toast.error("Suppression impossible."); }
  };

  const quickStatus = async (id, status) => {
    try {
      const { data } = await api.post(`/admin/content/${id}/status`, { status });
      setItems((s) => s.map((i) => (i.id === id ? data : i)));
      toast.success(status === "published" ? "Publié." : "Dépublié.");
    } catch { toast.error("Action impossible."); }
  };

  const isContentView = !["keywords", "media", "scheduled"].includes(view);
  const filtered = search.trim()
    ? items.filter((i) => JSON.stringify(i).toLowerCase().includes(search.trim().toLowerCase()))
    : items;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Nav />
      <main className="pt-28 pb-24 px-6 md:px-12 max-w-[1500px] mx-auto">
        <header className="flex items-end justify-between flex-wrap gap-6 border-b border-[#EAE8E3] pb-8">
          <div>
            <p className="label-eyebrow">Back-office SEO</p>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-3">Centre de contenu</h1>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/admin/proposals")} className="text-[11px] uppercase tracking-[0.24em] text-[#737373] hover:text-[#1A1B18]">Devis premium</button>
            <button onClick={() => navigate("/admin/settings")} data-testid="nav-site-settings" className="text-[11px] uppercase tracking-[0.24em] text-[#737373] hover:text-[#1A1B18]">Réglages du site</button>
            <button onClick={() => { logout(); navigate("/admin/login"); }} title={user?.email} className="text-[#737373] hover:text-[#1A1B18]"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="space-y-1" data-testid="content-sidebar">
            <p className="label-eyebrow px-3 mb-2">Types de contenu</p>
            {templates.map((t) => {
              const meta = TYPE_META[t.content_type] || { label: t.content_type, icon: PenLine };
              const Icon = meta.icon;
              const active = view === t.content_type;
              return (
                <button key={t.content_type} onClick={() => setView(t.content_type)} data-testid={`content-nav-${t.content_type}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors ${active ? "bg-[#1A1B18] text-white" : "hover:bg-white text-[#1A1B18]"}`}>
                  <Icon className={`h-4 w-4 ${active ? "text-[#E4DCCF]" : "text-[#96836B]"}`} strokeWidth={1.6} /> {meta.label}
                </button>
              );
            })}
            <p className="label-eyebrow px-3 mt-6 mb-2">Outils SEO</p>
            {[
              { key: "keywords", label: "Mots-clés SEO", icon: KeyRound },
              { key: "scheduled", label: "Publications programmées", icon: CalendarClock },
              { key: "media", label: "Médias", icon: ImageIcon },
            ].map(({ key, label, icon: Icon }) => {
              const active = view === key;
              return (
                <button key={key} onClick={() => setView(key)} data-testid={`content-nav-${key}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors ${active ? "bg-[#1A1B18] text-white" : "hover:bg-white text-[#1A1B18]"}`}>
                  <Icon className={`h-4 w-4 ${active ? "text-[#E4DCCF]" : "text-[#96836B]"}`} strokeWidth={1.6} /> {label}
                </button>
              );
            })}
          </aside>

          {/* Main */}
          <section>
            <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
              <h2 className="font-serif-display text-2xl">
                {view === "keywords" ? "Mots-clés SEO" : view === "media" ? "Bibliothèque média" : view === "scheduled" ? "Publications programmées" : (TYPE_META[view]?.label || view)}
              </h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#96836B]" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="luxury-input pl-9 w-56" data-testid="content-search" />
                </div>
                {isContentView && (
                  <>
                    <GeneratePackDialog onDone={refresh} navigate={navigate} />
                    <Button onClick={createItem} data-testid="btn-new-content" className="rounded-none bg-[#1A1B18] hover:bg-[#96836B] text-white text-[11px] uppercase tracking-[0.24em] px-5 py-5">
                      <Plus className="h-4 w-4 mr-2" /> Nouveau
                    </Button>
                  </>
                )}
                {view === "keywords" && <KeywordAdd onAdded={refresh} />}
              </div>
            </div>

            {loading ? (
              <p className="text-sm text-[#737373]">Chargement…</p>
            ) : view === "media" ? (
              <MediaGrid items={filtered} />
            ) : view === "keywords" ? (
              <KeywordTable items={filtered} onChange={refresh} />
            ) : filtered.length === 0 ? (
              <div className="text-center py-24 border border-dashed border-[#EAE8E3] bg-white">
                <p className="font-serif-display text-2xl">Aucun contenu ici</p>
                <p className="text-sm text-[#737373] mt-2">{isContentView ? "Cliquez sur « Nouveau » pour démarrer." : "Rien de programmé pour l'instant."}</p>
              </div>
            ) : (
              <div className="bg-white border border-[#EAE8E3] divide-y divide-[#EAE8E3]" data-testid="content-list">
                {filtered.map((i) => {
                  const st = STATUS_LABEL[i.status] || STATUS_LABEL.draft;
                  const prefix = prefixes[i.content_type] || "content";
                  return (
                    <article key={i.id} data-testid={`content-row-${i.id}`} className="flex items-center gap-4 p-4 hover:bg-[#FAF9F6]">
                      {i.cover_image ? <img src={i.cover_image} alt="" loading="lazy" className="w-16 h-12 object-cover border border-[#EAE8E3]" /> : <div className="w-16 h-12 bg-[#F2EFE9] border border-[#EAE8E3]" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{i.title}</p>
                        <p className="text-xs text-[#737373]">/{prefix}/{i.slug} · modifié le {format(parseISO(i.updated_at), "d MMM yyyy", { locale: fr })}{i.scheduled_at ? ` · programmé le ${format(parseISO(i.scheduled_at), "d MMM HH:mm", { locale: fr })}` : ""}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-1 shrink-0 ${st.cls}`}>{st.label}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button onClick={() => navigate(`/admin/content/${i.id}`)} data-testid={`btn-edit-content-${i.id}`} className="rounded-none bg-[#1A1B18] hover:bg-[#96836B] text-white text-[10px] uppercase tracking-[0.2em] py-2 px-3 h-auto"><Pencil className="h-3 w-3 mr-1.5" /> Éditer</Button>
                        {i.status === "published"
                          ? <a href={`/${prefix}/${i.slug}`} target="_blank" rel="noreferrer" title="Voir la page" className="text-[#737373] hover:text-[#1A1B18] p-2"><ExternalLink className="h-4 w-4" /></a>
                          : <button onClick={() => quickStatus(i.id, "published")} title="Publier" className="text-[10px] uppercase tracking-wider text-[#96836B] hover:text-[#1A1B18] px-2">Publier</button>}
                        <button onClick={() => removeItem(i.id)} title="Supprimer" className="text-[#737373] hover:text-[#7A4B3B] p-2"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

const GeneratePackDialog = ({ onDone, navigate }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [cover, setCover] = useState("");
  const [publish, setPublish] = useState(true);
  const [withVisuals, setWithVisuals] = useState(true);
  const [running, setRunning] = useState(false);

  const poll = (jobId) => new Promise((resolve, reject) => {
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/content/generate-pack/${jobId}`);
        if (data.status === "done") { clearInterval(iv); resolve(data.result); }
        else if (data.status === "error") { clearInterval(iv); reject(new Error(data.error || "Erreur")); }
      } catch (e) { clearInterval(iv); reject(e); }
    }, 3000);
  });

  const generate = async () => {
    if (!name.trim()) { toast.error("Indiquez une destination."); return; }
    setRunning(true);
    try {
      const images = cover.trim() ? { destination: cover.trim() } : {};
      const { data } = await api.post("/admin/content/generate-pack", {
        name: name.trim(), notes: notes.trim(), images, publish, featured: publish,
        with_visuals: withVisuals && !cover.trim(),
      });
      toast.info(withVisuals
        ? "Génération IA lancée — contenu SEO/GEO + visuels, comptez 4 à 8 minutes…"
        : "Génération IA lancée — contenu approfondi SEO/GEO, comptez 3 à 6 minutes…");
      const result = await poll(data.job_id);
      toast.success(`Pack « ${result.destination_title} » généré (${result.count} contenus).`);
      setOpen(false);
      setName(""); setNotes(""); setCover("");
      onDone?.();
      if (result.destination_id) navigate(`/admin/content/${result.destination_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Génération impossible.");
    } finally { setRunning(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !running && setOpen(o)}>
      <DialogTrigger asChild>
        <Button data-testid="btn-generate-pack" variant="outline" className="rounded-none border-[#96836B] text-[#1A1B18] hover:bg-[#96836B] hover:text-white text-[11px] uppercase tracking-[0.24em] px-5 py-5">
          <Wand2 className="h-4 w-4 mr-2" /> Pack IA
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg rounded-none bg-[#FAF9F6]" data-testid="generate-pack-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Générer un pack destination (IA)</DialogTitle>
          <DialogDescription className="text-sm text-[#737373]">
            L'IA crée en un clic une page destination pilier + son guide, son voyage de noces et sa FAQ, tous rattachés automatiquement.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <label className="label-eyebrow">Destination</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={running} placeholder="Ex. Maldives, Sicile, Costa Rica…" className="luxury-input" data-testid="pack-name" />
          </div>
          <div className="space-y-1.5">
            <label className="label-eyebrow">Instructions (facultatif)</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={running} placeholder="Angle éditorial, saison, type de clientèle, points à mettre en avant…" className="luxury-input min-h-[70px]" data-testid="pack-notes" />
          </div>
          <div className="space-y-1.5">
            <label className="label-eyebrow">Image de couverture (URL, facultatif)</label>
            <Input value={cover} onChange={(e) => setCover(e.target.value)} disabled={running} placeholder="https://… (sinon à ajouter ensuite dans l'éditeur)" className="luxury-input" data-testid="pack-cover" />
          </div>
          <label className="flex items-center gap-2.5 text-sm cursor-pointer">
            <Checkbox checked={publish} onCheckedChange={(v) => setPublish(!!v)} disabled={running} data-testid="pack-publish" />
            Publier et mettre en avant sur l'accueil immédiatement
          </label>
          <label className="flex items-center gap-2.5 text-sm cursor-pointer">
            <Checkbox checked={withVisuals} onCheckedChange={(v) => setWithVisuals(!!v)} disabled={running || !!cover.trim()} data-testid="pack-visuals" />
            Générer les visuels IA de la destination (couverture + illustrations)
          </label>
          <Button onClick={generate} disabled={running} data-testid="pack-generate-btn" className="w-full rounded-none bg-[#1A1B18] hover:bg-[#96836B] text-white text-[11px] uppercase tracking-[0.24em] py-6">
            {running ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Génération en cours…</> : <><Wand2 className="h-4 w-4 mr-2" /> Générer le pack</>}
          </Button>
          {running && <p className="text-[11px] text-[#737373] text-center">Vous pouvez patienter ici — la génération se poursuit même si vous fermez cette fenêtre.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MediaGrid = ({ items }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="media-grid">
    {items.length === 0 && <p className="text-sm text-[#737373] col-span-full">Aucun média. Les images importées dans les contenus apparaîtront ici.</p>}
    {items.map((m) => (
      <figure key={m.id} className="bg-white border border-[#EAE8E3]">
        <img src={m.url} alt={m.alt || ""} loading="lazy" className="w-full aspect-[4/3] object-cover" />
        <figcaption className="p-2 text-[11px] text-[#737373] truncate">{m.alt || m.original_filename} · {m.width}×{m.height}</figcaption>
      </figure>
    ))}
  </div>
);

const KeywordAdd = ({ onAdded }) => {
  const [val, setVal] = useState("");
  const add = async () => {
    if (!val.trim()) return;
    try { await api.post("/admin/keywords", { primary: val.trim() }); setVal(""); onAdded(); toast.success("Mot-clé ajouté."); }
    catch { toast.error("Ajout impossible."); }
  };
  return (
    <div className="flex items-center gap-2">
      <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Nouveau mot-clé…" className="luxury-input w-48" data-testid="keyword-input" />
      <Button onClick={add} data-testid="btn-add-keyword" className="rounded-none bg-[#1A1B18] hover:bg-[#96836B] text-white text-[11px] uppercase tracking-[0.24em] px-4 py-5"><Plus className="h-4 w-4" /></Button>
    </div>
  );
};

const KeywordTable = ({ items, onChange }) => {
  const upd = async (id, patch) => { try { await api.patch(`/admin/keywords/${id}`, patch); onChange(); } catch { toast.error("Échec."); } };
  const del = async (id) => { try { await api.delete(`/admin/keywords/${id}`); onChange(); } catch { toast.error("Échec."); } };
  if (!items.length) return <p className="text-sm text-[#737373]">Aucun mot-clé. Ajoutez-en pour piloter votre stratégie SEO.</p>;
  return (
    <div className="bg-white border border-[#EAE8E3] divide-y divide-[#EAE8E3]" data-testid="keyword-table">
      {items.map((k) => (
        <div key={k.id} className="flex items-center gap-4 p-4">
          <div className="flex-1 min-w-0">
            <p className="font-medium">{k.primary}</p>
            {k.secondary?.length > 0 && <p className="text-xs text-[#737373]">{k.secondary.join(" · ")}</p>}
          </div>
          <select value={k.priority} onChange={(e) => upd(k.id, { priority: e.target.value })} className="text-xs border border-[#EAE8E3] px-2 py-1.5 bg-white">
            <option value="low">Priorité basse</option><option value="medium">Priorité moyenne</option><option value="high">Priorité haute</option>
          </select>
          <select value={k.status} onChange={(e) => upd(k.id, { status: e.target.value })} className="text-xs border border-[#EAE8E3] px-2 py-1.5 bg-white">
            <option value="idea">Idée</option><option value="in_progress">En cours</option><option value="published">Publié</option>
          </select>
          <button onClick={() => del(k.id)} className="text-[#737373] hover:text-[#7A4B3B] p-2"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
};
